import { css } from '@emotion/react';
import { FaxDraftLite } from '@weave/schema-gen-ts/dist/schemas/fax/draft/v1/fax_draft_service.pb';
import { FaxTypes } from '@frontend/api-fax';
import { Chips } from '@frontend/chips';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { ListRow, useModalControl, usePopoverMenu, useTooltip } from '@frontend/design-system';
import { useFaxActions } from '../../hooks/use-fax-actions';
import { useDeleteConfirmationShallowStore } from '../../stores/delete-confirmation-store';
import { DeleteFaxConfirmationModal } from './delete-fax-confirmation-modal';
import { FaxInboxListItemLead } from './fax-inbox-list-item-lead';
import { FaxListRow } from './fax-list-row';

export const DraftInboxListItem = ({ listItem }: { listItem: FaxDraftLite }) => {
  const { locationId, id: draftId, hasCoversheet, hasUploadedFiles: hasFiles } = listItem;
  const draftLocationId = locationId ?? '';
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const { getLocationName, selectedLocationIds } = useAppScopeStore();
  const { deleteDraft } = useFaxActions();
  const { modalProps: deleteModalProps, openModal: openDeleteModal } = useModalControl();
  const { skipDraftDeleteConfirmation } = useDeleteConfirmationShallowStore('skipDraftDeleteConfirmation');

  const {
    Tooltip,
    triggerProps: tooltipTriggerProps,
    tooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'left-start',
    offset: Number(theme.spacing(1).replace('px', '')),
  });

  const actionsPopover = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: {
      offset: {
        alignmentAxis: 0,
        mainAxis: 0,
      },
    },
  });

  if (!draftId) return null;

  // preview and download not currently working on BE, will uncomment when fixed
  const actions = [
    {
      actionIcon: () => <Icon name='trash' css={{ color: theme.colors.neutral70 }} />,
      label: t('Delete Draft'),
      action: () => {
        if (skipDraftDeleteConfirmation) {
          deleteDraft(draftId, draftLocationId);
        } else {
          openDeleteModal();
        }
      },
      trackingId: `${FaxPrefixes.Drafts}-delete-button`,
    },
  ];

  return (
    <div>
      <FaxListRow
        css={actionableListRowStyles}
        Lead={
          <FaxInboxListItemLead
            isBulkSelect={false}
            personName={t('Draft')}
            locationId={locationId}
            isCheckboxSelected={false}
            isInbound={false}
            status={FaxTypes.FaxStatus.DRAFT}
          />
        }
        actions={actions}
        actionsPopoverProps={actionsPopover}
        Title={
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 0,
              gap: theme.spacing(1),
            }}
            {...tooltipTriggerProps}
          >
            <ListRow.Content.Title
              css={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              isBold={false}
            >
              {t('Draft')}
            </ListRow.Content.Title>
            <Tooltip {...tooltipProps}>{t('Draft')}</Tooltip>
          </div>
        }
        Content={
          <>
            {selectedLocationIds.length > 1 && (
              <Chips.LocationChip
                css={{
                  fontSize: theme.fontSize(12),
                  color: theme.colorsV2.black(60),
                  backgroundColor: theme.colorsV2.black(10),
                }}
                className='inbox-item-chip'
              >
                {getLocationName(draftLocationId)}
              </Chips.LocationChip>
            )}
          </>
        }
        timestamp={listItem.createdAt}
        tabIndex={0}
        key={draftId}
        id={draftId}
        onClick={() => {
          if (!hasCoversheet && !hasFiles) {
            navigate({
              to: '/fax/drafts/$draftId/cover',
              params: { draftId },
              search: {
                selectedLocationId: selectedLocationIds.length > 1 ? locationId : undefined,
              },
            });
          } else if (hasCoversheet && !hasFiles) {
            navigate({
              to: '/fax/drafts/$draftId/upload',
              params: { draftId },
              search: {
                selectedLocationId: selectedLocationIds.length > 1 ? locationId : undefined,
              },
            });
          } else {
            navigate({
              to: '/fax/drafts/$draftId/finalize',
              params: { draftId },
              search: {
                selectedLocationId: selectedLocationIds.length > 1 ? locationId : undefined,
              },
            });
          }
        }}
        data-trackingid={`${FaxPrefixes.List}-inbox-item`}
      />
      <DeleteFaxConfirmationModal
        modalProps={deleteModalProps}
        onDelete={() => deleteDraft(draftId, draftLocationId)}
        deleteType='draft'
      />
    </div>
  );
};

const actionableListRowStyles = css`
  padding: ${theme.spacing(2)};
  padding-left: ${theme.spacing(3)};
  overflow-x: hidden;

  &:hover .inbox-item-chip {
    background-color: ${theme.colors.white};
  }
`;
